import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Users, UserPlus, X, Crown, Eye, MessageSquare, Edit, Mail } from "lucide-react";
import { apiClient } from "@/lib/api";
import type { Collaborator, PermissionLevel, ProjectCollaboration } from "@/types/collaboration";
import { useToast } from "@/hooks/use-toast";

interface CollaborationPanelProps {
  projectId: string;
  isOwner: boolean;
  currentUserId: string;
}

export const CollaborationPanel = ({
  projectId,
  isOwner,
  currentUserId,
}: CollaborationPanelProps) => {
  const [collaboration, setCollaboration] = useState<ProjectCollaboration | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingCollaborator, setIsAddingCollaborator] = useState(false);
  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState("");
  const [newCollaboratorPermission, setNewCollaboratorPermission] = useState<PermissionLevel>("view");
  const { toast } = useToast();

  useEffect(() => {
    loadCollaboration();
  }, [projectId]);

  const loadCollaboration = async () => {
    try {
      setIsLoading(true);
      const data = await apiClient.getProjectCollaboration(projectId) as ProjectCollaboration;
      setCollaboration(data);
    } catch (error: any) {
      console.error("Failed to load collaboration:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to load collaboration data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCollaborator = async () => {
    if (!newCollaboratorEmail.trim()) return;

    try {
      await apiClient.addCollaborator(projectId, newCollaboratorEmail, newCollaboratorPermission);
      toast({
        title: "Success",
        description: `Collaborator ${newCollaboratorEmail} added successfully`,
      });
      setNewCollaboratorEmail("");
      setNewCollaboratorPermission("view");
      setIsAddingCollaborator(false);
      loadCollaboration();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add collaborator",
        variant: "destructive",
      });
    }
  };

  const handleRemoveCollaborator = async (userId: string) => {
    if (!confirm("Are you sure you want to remove this collaborator?")) return;

    try {
      await apiClient.removeCollaborator(projectId, userId);
      toast({
        title: "Success",
        description: "Collaborator removed successfully",
      });
      loadCollaboration();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to remove collaborator",
        variant: "destructive",
      });
    }
  };

  const handleUpdatePermission = async (userId: string, permission: PermissionLevel) => {
    try {
      await apiClient.updateCollaboratorPermission(projectId, userId, permission);
      toast({
        title: "Success",
        description: "Permission updated successfully",
      });
      loadCollaboration();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update permission",
        variant: "destructive",
      });
    }
  };

  const getPermissionIcon = (permission: PermissionLevel) => {
    switch (permission) {
      case "view":
        return <Eye className="h-3 w-3" />;
      case "comment":
        return <MessageSquare className="h-3 w-3" />;
      case "edit":
        return <Edit className="h-3 w-3" />;
    }
  };

  const getPermissionLabel = (permission: PermissionLevel) => {
    switch (permission) {
      case "view":
        return "View Only";
      case "comment":
        return "Can Comment";
      case "edit":
        return "Can Edit";
    }
  };

  const getPermissionColor = (permission: PermissionLevel) => {
    switch (permission) {
      case "view":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20";
      case "comment":
        return "bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20";
      case "edit":
        return "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20";
    }
  };

  const getInitials = (name: string, email: string) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Users className="h-4 w-4 animate-pulse" />
          <span className="text-sm">Loading collaborators...</span>
        </div>
      </Card>
    );
  }

  if (!collaboration) {
    return null;
  }

  return (
    <Card className="p-4">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Collaborators</h3>
            <Badge variant="secondary" className="text-xs">
              {collaboration.collaborators.length}
            </Badge>
          </div>
          
          {isOwner && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsAddingCollaborator(!isAddingCollaborator)}
            >
              <UserPlus className="h-4 w-4 mr-1" />
              Add
            </Button>
          )}
        </div>

        {/* Add Collaborator Form */}
        {isAddingCollaborator && isOwner && (
          <div className="p-3 bg-secondary/50 rounded-lg space-y-3">
            <Input
              type="email"
              placeholder="Email address"
              value={newCollaboratorEmail}
              onChange={(e) => setNewCollaboratorEmail(e.target.value)}
              className="text-sm"
            />
            <div className="flex gap-2">
              <Select
                value={newCollaboratorPermission}
                onValueChange={(value) => setNewCollaboratorPermission(value as PermissionLevel)}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View Only</SelectItem>
                  <SelectItem value="comment">Can Comment</SelectItem>
                  <SelectItem value="edit">Can Edit</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={handleAddCollaborator}
                disabled={!newCollaboratorEmail.trim()}
              >
                Add
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsAddingCollaborator(false);
                  setNewCollaboratorEmail("");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Collaborators List */}
        <div className="space-y-3">
          {collaboration.collaborators.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No collaborators yet</p>
              <p className="text-xs mt-1">Add team members to collaborate on this project</p>
            </div>
          ) : (
            collaboration.collaborators.map((collaborator) => (
              <div
                key={collaborator.user_id}
                className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors"
              >
                {/* Avatar */}
                <Avatar className="h-10 w-10 border-2 border-border">
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                    {getInitials(collaborator.name || "", collaborator.email)}
                  </AvatarFallback>
                </Avatar>

                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold truncate">
                      {collaborator.name || collaborator.email.split("@")[0]}
                    </p>
                    {collaborator.is_owner && (
                      <Crown className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" />
                    )}
                    {collaborator.user_id === currentUserId && (
                      <Badge variant="secondary" className="text-xs px-1.5 py-0">
                        You
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <Mail className="h-3 w-3" />
                    <span className="truncate">{collaborator.email}</span>
                  </div>
                </div>

                {/* Permission Badge & Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {collaborator.is_owner ? (
                    <Badge className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20 text-xs px-2 py-1">
                      <Crown className="h-3 w-3 mr-1" />
                      Owner
                    </Badge>
                  ) : isOwner ? (
                    <>
                      <Select
                        value={collaborator.permission}
                        onValueChange={(value) =>
                          handleUpdatePermission(collaborator.user_id, value as PermissionLevel)
                        }
                      >
                        <SelectTrigger className="h-8 text-xs w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">
                            <div className="flex items-center gap-2">
                              <Eye className="h-3.5 w-3.5" />
                              View Only
                            </div>
                          </SelectItem>
                          <SelectItem value="comment">
                            <div className="flex items-center gap-2">
                              <MessageSquare className="h-3.5 w-3.5" />
                              Can Comment
                            </div>
                          </SelectItem>
                          <SelectItem value="edit">
                            <div className="flex items-center gap-2">
                              <Edit className="h-3.5 w-3.5" />
                              Can Edit
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => handleRemoveCollaborator(collaborator.user_id)}
                        title="Remove collaborator"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <Badge
                      variant="outline"
                      className={`text-xs px-2 py-1 ${getPermissionColor(collaborator.permission)}`}
                    >
                      <span className="flex items-center gap-1">
                        {getPermissionIcon(collaborator.permission)}
                        {getPermissionLabel(collaborator.permission)}
                      </span>
                    </Badge>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Info Text */}
        <div className="text-xs text-muted-foreground pt-2 border-t">
          <p className="font-medium mb-1">Permission Levels:</p>
          <ul className="space-y-0.5 ml-4">
            <li>• <strong>View:</strong> See all content and recommendations</li>
            <li>• <strong>Comment:</strong> Add sticky notes on recommendations</li>
            <li>• <strong>Edit:</strong> Accept/decline recommendations, upload files</li>
          </ul>
        </div>
      </div>
    </Card>
  );
};